// Enrollment utility functions
import { validateUserEnrollment } from '../types/UserEnrollment';
import { sanitizeInput } from './validation';
import config from '../config';

const { API_URL } = config;

/**
 * Enroll a user in a program
 * @param {string} userEmail - User's email address
 * @param {string} programName - Program name to enroll in
 * @param {string} [userToken] - Optional user authentication token
 * @returns {Promise<{success: boolean, message: string, data?: Object}>}
 */
export const enrollUserInProgram = async (userEmail, programName, userToken = null) => {
  try {
    const enrollmentData = {
      action: 'enroll',
      user_email: sanitizeInput(userEmail),
      program_name: sanitizeInput(programName),
      enrollment_date: new Date().toISOString()
    };

    // Validate enrollment data
    const validation = validateUserEnrollment({
      ...enrollmentData,
      status: 'active'
    });

    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join(', ');
      return {
        success: false,
        message: 'Validation Error: ' + errorMessages
      };
    }

    const response = await fetch(`${API_URL}/admin/enrollments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {})
      },
      body: JSON.stringify(enrollmentData)
    });

    const result = await response.json();

    if (response.ok) {
      return {
        success: true,
        message: 'Successfully enrolled in program',
        data: enrollmentData
      };
    } else {
      return {
        success: false,
        message: result.error || result.message || 'Enrollment failed'
      };
    }
  } catch (error) {
    console.error('Enrollment error:', error);
    return {
      success: false,
      message: 'Network error: ' + error.message
    };
  }
};

/**
 * Update enrollment status
 * @param {string} userEmail - User's email address
 * @param {string} programName - Program name
 * @param {string} status - New status (active, inactive, completed)
 * @param {string} [userToken] - Optional user authentication token
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const updateEnrollmentStatus = async (userEmail, programName, status, userToken = null) => {
  try {
    const updateData = {
      action: 'update_status',
      user_email: sanitizeInput(userEmail),
      program_name: sanitizeInput(programName),
      status: status
    };

    const response = await fetch(`${API_URL}/admin/enrollments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {})
      },
      body: JSON.stringify(updateData)
    });

    const result = await response.json();

    if (response.ok) {
      return {
        success: true,
        message: 'Enrollment status updated successfully'
      };
    } else {
      return {
        success: false,
        message: result.error || result.message || 'Status update failed'
      };
    }
  } catch (error) {
    console.error('Status update error:', error);
    return {
      success: false,
      message: 'Network error: ' + error.message
    };
  }
};

/**
 * Update enrollment progress
 * @param {string} userEmail - User's email address
 * @param {string} programName - Program name
 * @param {string} assessmentName - Assessment that was completed
 * @param {string} [userToken] - Optional user authentication token
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const updateEnrollmentProgress = async (userEmail, programName, assessmentName, userToken = null) => {
  try {
    const progressData = {
      action: 'update_progress',
      user_email: sanitizeInput(userEmail),
      program_name: sanitizeInput(programName),
      assessment_name: sanitizeInput(assessmentName),
      progress_type: 'assessment_completed'
    };

    const response = await fetch(`${API_URL}/admin/enrollments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {})
      },
      body: JSON.stringify(progressData)
    });

    const result = await response.json();

    if (response.ok) {
      return {
        success: true,
        message: 'Progress updated successfully'
      };
    } else {
      return {
        success: false,
        message: result.error || result.message || 'Progress update failed'
      };
    }
  } catch (error) {
    console.error('Progress update error:', error);
    return {
      success: false,
      message: 'Network error: ' + error.message
    };
  }
};

/**
 * Update enrollment activity timestamp
 * @param {string} userEmail - User's email address
 * @param {string} programName - Program name
 * @param {string} [userToken] - Optional user authentication token
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const updateEnrollmentActivity = async (userEmail, programName, userToken = null) => {
  try {
    const activityData = {
      action: 'update_progress',
      user_email: sanitizeInput(userEmail),
      program_name: sanitizeInput(programName),
      progress_type: 'activity_update'
    };

    const response = await fetch(`${API_URL}/admin/enrollments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {})
      },
      body: JSON.stringify(activityData)
    });

    const result = await response.json();

    return {
      success: response.ok,
      message: response.ok ? 'Activity updated' : (result.error || 'Activity update failed')
    };
  } catch (error) {
    console.error('Activity update error:', error);
    return {
      success: false,
      message: 'Network error: ' + error.message
    };
  }
};

/**
 * Load user enrollments with optional filtering
 * @param {string} [userEmail] - Filter by user email
 * @param {string} [programName] - Filter by program name
 * @param {string} [status] - Filter by status
 * @param {boolean} [includeProgress] - Include progress statistics
 * @returns {Promise<{success: boolean, message: string, data?: Array}>}
 */
export const loadEnrollments = async (userEmail = null, programName = null, status = null, includeProgress = true) => {
  try {
    const params = new URLSearchParams();
    
    if (userEmail) params.append('user_email', userEmail);
    if (programName) params.append('program_name', programName);
    if (status) params.append('status', status);
    if (includeProgress !== undefined) params.append('include_progress', includeProgress.toString());

    const url = `${API_URL}/admin/enrollments${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: true,
          message: 'No enrollments found',
          data: []
        };
      }
      throw new Error(`HTTP ${response.status}`);
    }

    const enrollments = await response.json();

    return {
      success: true,
      message: 'Enrollments loaded successfully',
      data: enrollments || []
    };
  } catch (error) {
    console.error('Error loading enrollments:', error);
    return {
      success: false,
      message: 'Error loading enrollments: ' + error.message,
      data: []
    };
  }
};

/**
 * Check if user is eligible for enrollment in a program
 * @param {Object} user - User object
 * @param {Object} program - Program object
 * @param {Array} userEnrollments - User's current enrollments
 * @returns {{eligible: boolean, reason?: string}}
 */
export const checkEnrollmentEligibility = (user, program, userEnrollments) => {
  // Validate inputs
  if (!user || !program) {
    return { eligible: false, reason: 'Invalid user or program data' };
  }

  if (!user.email) {
    return { eligible: false, reason: 'User email is required for enrollment' };
  }

  // Check if user is already enrolled
  const existingEnrollment = userEnrollments.find(e => e.program_name === program.name);
  if (existingEnrollment) {
    if (existingEnrollment.status === 'active') {
      return { eligible: false, reason: 'Already enrolled in this program' };
    } else if (existingEnrollment.status === 'completed') {
      return { eligible: false, reason: 'Program already completed' };
    }
    // If status is 'inactive', allow re-enrollment (fall through to eligible: true)
  }

  // Check if program is active
  if (program.status !== 'active') {
    return { eligible: false, reason: 'Program is not currently active' };
  }

  // Validate program has required fields
  if (!program.assessment_name) {
    return { eligible: false, reason: 'Program configuration is incomplete (missing assessment)' };
  }

  // Check program requirements with proper validation
  if (program.requirements && program.requirements.length > 0) {
    console.log('Checking program requirements:', program.requirements);
    const requirementCheck = validateProgramRequirements(user, program.requirements, userEnrollments);
    console.log('Requirements check result:', requirementCheck);
    if (!requirementCheck.eligible) {
      return requirementCheck;
    }
  }

  console.log('All checks passed - user is eligible');
  return { eligible: true };
};

/**
 * Validate program requirements against user profile and enrollments
 * @param {Object} user - User object
 * @param {Array} requirements - Array of requirement strings
 * @param {Array} userEnrollments - User's current enrollments
 * @returns {{eligible: boolean, reason?: string}}
 */
export const validateProgramRequirements = (user, requirements, userEnrollments) => {
  if (!requirements || requirements.length === 0) {
    return { eligible: true };
  }

  // For now, implement basic requirement validation
  // In a real system, you would have more sophisticated requirement checking
  for (const requirement of requirements) {
    const reqLower = requirement.toLowerCase();
    
    // Check for prerequisite program completion
    if (reqLower.includes('complete') && reqLower.includes('program')) {
      const programMatch = reqLower.match(/complete\s+(.+?)\s+program/);
      if (programMatch) {
        const prerequisiteProgram = programMatch[1].trim();
        const hasCompleted = userEnrollments.some(e => 
          e.program_name.toLowerCase().includes(prerequisiteProgram) && 
          e.status === 'completed'
        );
        
        if (!hasCompleted) {
          return { 
            eligible: false, 
            reason: `Prerequisite required: ${requirement}` 
          };
        }
      }
    }
    
    // Check for minimum experience requirements
    if (reqLower.includes('experience') || reqLower.includes('years')) {
      // For now, we'll assume this is met
      // In a real system, you would check user profile data
      console.log('Experience requirement found:', requirement);
    }
    
    // Check for certification requirements
    if (reqLower.includes('certification') || reqLower.includes('certified')) {
      // For now, we'll assume this is met
      // In a real system, you would check user certifications
      console.log('Certification requirement found:', requirement);
    }
  }

  return { eligible: true };
};

/**
 * Get enrollment statistics for analytics
 * @param {Array} enrollments - Array of enrollments
 * @returns {Object} Statistics object
 */
export const getEnrollmentStatistics = (enrollments) => {
  const stats = {
    total: enrollments.length,
    active: 0,
    completed: 0,
    inactive: 0,
    recentActivity: 0,
    averageDuration: 0
  };

  let totalDuration = 0;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  enrollments.forEach(enrollment => {
    // Count by status
    if (enrollment.status === 'active') stats.active++;
    else if (enrollment.status === 'completed') stats.completed++;
    else if (enrollment.status === 'inactive') stats.inactive++;

    // Check recent activity
    if (enrollment.progress?.last_activity) {
      const lastActivity = new Date(enrollment.progress.last_activity);
      if (lastActivity > sevenDaysAgo) {
        stats.recentActivity++;
      }
    }

    // Calculate duration
    if (enrollment.enrollment_date) {
      const enrollmentDate = new Date(enrollment.enrollment_date);
      const duration = (new Date() - enrollmentDate) / (1000 * 60 * 60 * 24); // days
      totalDuration += duration;
    }
  });

  stats.averageDuration = enrollments.length > 0 ? Math.round(totalDuration / enrollments.length) : 0;

  return stats;
};